import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";

function publicClient() {
  return createClient(process.env.SUPABASE_URL!, process.env.SUPABASE_PUBLISHABLE_KEY!, {
    auth: { persistSession: false, autoRefreshToken: false },
  });
}

export type BlogSummary = {
  slug: string;
  title: string;
  description: string;
  category: string;
  keywords: string[];
  published_at: string;
  reading_minutes: number;
  author: string;
};

export type BlogPost = BlogSummary & {
  id: string;
  content_md: string;
  jsonld_str: string | null;
  updated_at: string;
};

export const listBlogPosts = createServerFn({ method: "GET" }).handler(async () => {
  const sb = publicClient();
  const { data, error } = await sb
    .from("blog_posts")
    .select("slug, title, description, category, keywords, published_at, reading_minutes, author")
    .lte("published_at", new Date().toISOString())
    .order("published_at", { ascending: false })
    .limit(200);
  if (error) throw new Error(error.message);
  return (data ?? []) as BlogSummary[];
});

export const getBlogPost = createServerFn({ method: "GET" })
  .inputValidator((input: { slug: string }) => {
    if (!input?.slug || typeof input.slug !== "string") throw new Error("slug required");
    return { slug: input.slug };
  })
  .handler(async ({ data }) => {
    const sb = publicClient();
    const { data: row, error } = await sb
      .from("blog_posts")
      .select("*")
      .eq("slug", data.slug)
      .lte("published_at", new Date().toISOString())
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!row) return null;
    const { jsonld, ...rest } = row as Record<string, unknown> & { jsonld: unknown };
    return {
      ...(rest as Omit<BlogPost, "jsonld_str">),
      jsonld_str: jsonld ? JSON.stringify(jsonld) : null,
    } satisfies BlogPost;
  });